/**
 * Skills Matrix Table Component
 * 
 * Interactive compliance-style matrix table.
 * Rows = candidates, Columns = criteria, Cells = color-coded 0–3 scores with evidence tooltips.
 */

import React, { useState, useMemo } from 'react';
import {
    Box,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TableSortLabel,
    Typography,
    Tooltip,
    Paper,
    Collapse,
    IconButton,
    Chip
} from '@mui/material';
import {
    ExpandMore as ExpandMoreIcon,
    ExpandLess as ExpandLessIcon
} from '@mui/icons-material';
import { SkillsMatrixResult } from '../services/skillsMatrix';

interface SkillsMatrixTableProps {
    matrixResult: SkillsMatrixResult;
}

/** Score → background color mapping */
const SCORE_COLORS: Record<number, string> = {
    0: 'rgba(229, 115, 115, 0.3)',   // red
    1: 'rgba(255, 183, 77, 0.3)',    // amber
    2: 'rgba(165, 214, 167, 0.3)',   // light green
    3: 'rgba(102, 187, 106, 0.4)',   // dark green
};

const SCORE_TEXT_COLORS: Record<number, string> = {
    0: '#ef5350',
    1: '#ffb74d',
    2: '#a5d6a7',
    3: '#66bb6a',
};

const SCORE_LABELS: Record<number, string> = {
    0: 'No evidence',
    1: 'Mentioned',
    2: 'Demonstrated',
    3: 'Extensive',
};

/** Category → short label for column group headers */
const CATEGORY_LABELS: Record<string, string> = {
    thematic: 'Thematic / Sector',
    donor: 'Donor Experience',
    geographic: 'Geographic',
    methods: 'Methods / Tools',
    languages: 'Languages',
    education: 'Education',
    experience: 'Experience',
    custom: 'Custom',
};

type SortField = 'name' | 'total' | string; // criterion id
type SortDir = 'asc' | 'desc';

const SkillsMatrixTable: React.FC<SkillsMatrixTableProps> = ({ matrixResult }) => {
    const [sortField, setSortField] = useState<SortField>('total');
    const [sortDir, setSortDir] = useState<SortDir>('desc');
    const [expandedRow, setExpandedRow] = useState<number | null>(null);

    const enabledCriteria = useMemo(
        () => matrixResult.criteria.filter(c => c.enabled !== false),
        [matrixResult.criteria]
    );

    // Sort results
    const sortedResults = useMemo(() => {
        const results = [...matrixResult.results];
        results.sort((a, b) => {
            let aVal: number | string;
            let bVal: number | string;

            if (sortField === 'name') {
                aVal = (a.associate_name || a.filename).toLowerCase();
                bVal = (b.associate_name || b.filename).toLowerCase();
                return sortDir === 'asc'
                    ? (aVal as string).localeCompare(bVal as string)
                    : (bVal as string).localeCompare(aVal as string);
            } else if (sortField === 'total') {
                aVal = a.total_score;
                bVal = b.total_score;
            } else {
                aVal = a.scores[sortField]?.score ?? -1;
                bVal = b.scores[sortField]?.score ?? -1;
            }

            return sortDir === 'asc' ? (aVal as number) - (bVal as number) : (bVal as number) - (aVal as number);
        });
        return results;
    }, [matrixResult.results, sortField, sortDir]);

    const handleSort = (field: SortField) => {
        if (sortField === field) {
            setSortDir(prev => prev === 'asc' ? 'desc' : 'asc');
        } else {
            setSortField(field);
            setSortDir('desc');
        }
    };

    const toggleExpand = (cvId: number) => {
        setExpandedRow(prev => prev === cvId ? null : cvId);
    };

    return (
        <TableContainer component={Paper} sx={{ bgcolor: 'background.paper', maxHeight: 'calc(100vh - 280px)', overflow: 'auto' }}>
            <Table stickyHeader size="small">
                <TableHead>
                    {/* Category group header row */}
                    <TableRow>
                        <TableCell
                            sx={{
                                bgcolor: '#1a1a2e',
                                borderBottom: '2px solid rgba(34, 184, 167, 0.3)',
                                position: 'sticky',
                                left: 0,
                                zIndex: 3,
                                minWidth: 180
                            }}
                        />
                        {enabledCriteria.map(c => (
                            <TableCell
                                key={c.id}
                                align="center"
                                sx={{
                                    bgcolor: '#1a1a2e',
                                    borderBottom: '2px solid rgba(34, 184, 167, 0.3)',
                                    fontSize: '0.65rem',
                                    color: 'rgba(255,255,255,0.5)',
                                    py: 0.5,
                                    px: 1
                                }}
                            >
                                {CATEGORY_LABELS[c.category] || c.category}
                            </TableCell>
                        ))}
                        <TableCell
                            align="center"
                            sx={{
                                bgcolor: '#1a1a2e',
                                borderBottom: '2px solid rgba(34, 184, 167, 0.3)',
                                fontSize: '0.65rem',
                                color: 'rgba(255,255,255,0.5)',
                                py: 0.5
                            }}
                        >
                            Overall
                        </TableCell>
                    </TableRow>
                    {/* Main header row with criterion labels */}
                    <TableRow>
                        <TableCell
                            sx={{
                                bgcolor: '#1e1e32',
                                position: 'sticky',
                                left: 0,
                                zIndex: 3,
                                minWidth: 180,
                                fontWeight: 700
                            }}
                        >
                            <TableSortLabel
                                active={sortField === 'name'}
                                direction={sortField === 'name' ? sortDir : 'asc'}
                                onClick={() => handleSort('name')}
                            >
                                Candidate
                            </TableSortLabel>
                        </TableCell>
                        {enabledCriteria.map(c => (
                            <TableCell
                                key={c.id}
                                align="center"
                                sx={{
                                    bgcolor: '#1e1e32',
                                    fontWeight: 600,
                                    fontSize: '0.75rem',
                                    px: 1,
                                    minWidth: 100,
                                    maxWidth: 140,
                                    whiteSpace: 'normal',
                                    lineHeight: 1.2
                                }}
                            >
                                <TableSortLabel
                                    active={sortField === c.id}
                                    direction={sortField === c.id ? sortDir : 'desc'}
                                    onClick={() => handleSort(c.id)}
                                >
                                    {c.label}
                                </TableSortLabel>
                            </TableCell>
                        ))}
                        <TableCell
                            align="center"
                            sx={{ bgcolor: '#1e1e32', fontWeight: 700, minWidth: 80 }}
                        >
                            <TableSortLabel
                                active={sortField === 'total'}
                                direction={sortField === 'total' ? sortDir : 'desc'}
                                onClick={() => handleSort('total')}
                            >
                                Total
                            </TableSortLabel>
                        </TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {sortedResults.map((candidate, rowIdx) => (
                        <React.Fragment key={candidate.cv_id}>
                            <TableRow
                                hover
                                sx={{
                                    '&:nth-of-type(odd)': { bgcolor: 'rgba(255,255,255,0.02)' },
                                    cursor: 'pointer'
                                }}
                                onClick={() => toggleExpand(candidate.cv_id)}
                            >
                                {/* Candidate name */}
                                <TableCell
                                    sx={{
                                        position: 'sticky',
                                        left: 0,
                                        bgcolor: rowIdx % 2 === 0 ? '#121418' : '#151820',
                                        zIndex: 1,
                                        borderRight: '1px solid rgba(255,255,255,0.08)'
                                    }}
                                >
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                        <IconButton size="small" sx={{ p: 0 }}>
                                            {expandedRow === candidate.cv_id ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
                                        </IconButton>
                                        <Typography variant="body2" fontWeight={600} noWrap>
                                            {candidate.associate_name || candidate.filename.replace(/\.pdf$/i, '')}
                                        </Typography>
                                    </Box>
                                </TableCell>

                                {/* Score cells */}
                                {enabledCriteria.map(c => {
                                    const s = candidate.scores[c.id];
                                    const score = s?.score ?? 0;
                                    const evidence = s?.evidence || 'No evidence';

                                    return (
                                        <Tooltip
                                            key={c.id}
                                            title={
                                                <Box sx={{ maxWidth: 300 }}>
                                                    <Typography variant="caption" fontWeight={700}>
                                                        {c.label}: {SCORE_LABELS[score]} ({score}/3)
                                                    </Typography>
                                                    <Typography variant="caption" display="block" sx={{ mt: 0.5 }}>
                                                        {evidence}
                                                    </Typography>
                                                </Box>
                                            }
                                            arrow
                                            placement="top"
                                        >
                                            <TableCell
                                                align="center"
                                                sx={{
                                                    bgcolor: SCORE_COLORS[score] || 'transparent',
                                                    borderLeft: '1px solid rgba(255,255,255,0.04)',
                                                    transition: 'background-color 0.2s',
                                                    '&:hover': {
                                                        bgcolor: SCORE_COLORS[score]?.replace(/0\.\d\)/, '0.55)') || 'rgba(255,255,255,0.05)'
                                                    }
                                                }}
                                            >
                                                <Typography
                                                    variant="body2"
                                                    fontWeight={700}
                                                    sx={{ color: SCORE_TEXT_COLORS[score] || 'inherit' }}
                                                >
                                                    {score}
                                                </Typography>
                                            </TableCell>
                                        </Tooltip>
                                    );
                                })}

                                {/* Total score cell */}
                                <TableCell
                                    align="center"
                                    sx={{
                                        fontWeight: 700,
                                        borderLeft: '2px solid rgba(34, 184, 167, 0.3)'
                                    }}
                                >
                                    <Typography variant="body2" fontWeight={700}>
                                        {candidate.total_score}/{candidate.max_possible}
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary">
                                        {candidate.percentage}%
                                    </Typography>
                                </TableCell>
                            </TableRow>

                            {/* Expanded evidence row */}
                            <TableRow>
                                <TableCell
                                    colSpan={enabledCriteria.length + 2}
                                    sx={{ p: 0, border: 0 }}
                                >
                                    <Collapse in={expandedRow === candidate.cv_id} timeout="auto" unmountOnExit>
                                        <Box sx={{ p: 2, bgcolor: 'rgba(34, 184, 167, 0.04)', borderBottom: '1px solid rgba(255,255,255,0.08)' }}>
                                            <Typography variant="subtitle2" gutterBottom color="primary.main">
                                                Evidence for {candidate.associate_name || candidate.filename.replace(/\.pdf$/i, '')}
                                            </Typography>
                                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                                                {enabledCriteria.map(c => {
                                                    const s = candidate.scores[c.id];
                                                    const score = s?.score ?? 0;
                                                    return (
                                                        <Box
                                                            key={c.id}
                                                            sx={{
                                                                flex: '1 1 280px',
                                                                maxWidth: 400,
                                                                p: 1.5,
                                                                borderRadius: 1,
                                                                bgcolor: 'rgba(255,255,255,0.03)',
                                                                border: `1px solid ${SCORE_TEXT_COLORS[score]}40`
                                                            }}
                                                        >
                                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                                                                <Chip
                                                                    label={score}
                                                                    size="small"
                                                                    sx={{
                                                                        bgcolor: SCORE_COLORS[score],
                                                                        color: SCORE_TEXT_COLORS[score],
                                                                        fontWeight: 700,
                                                                        minWidth: 28,
                                                                        height: 22
                                                                    }}
                                                                />
                                                                <Typography variant="caption" fontWeight={600}>
                                                                    {c.label}
                                                                </Typography>
                                                            </Box>
                                                            <Typography variant="caption" color="text.secondary">
                                                                {s?.evidence || 'No evidence found'}
                                                            </Typography>
                                                        </Box>
                                                    );
                                                })}
                                            </Box>
                                        </Box>
                                    </Collapse>
                                </TableCell>
                            </TableRow>
                        </React.Fragment>
                    ))}
                </TableBody>
            </Table>
        </TableContainer>
    );
};

export default SkillsMatrixTable;
